import { toast } from 'react-toastify';
import React, { useState, useRef, useEffect, useCallback } from 'react';
// @ts-ignore
import { fabric } from 'fabric';
import { Save, RefreshCw, Upload, Plus, Settings, Trash2, AlignLeft, AlignCenter, AlignRight, AlignStartVertical, AlignCenterVertical, AlignEndVertical } from 'lucide-react';
import TypeLegend from './TypeLegend';
import AddSpaceModal from './AddSpaceModal';
import SpaceDefaultsModal from './SpaceDefaultsModal';
import { EntityType, FloorLayoutImage, FloorSpace, SpaceUpdateData, SpaceObjectData } from '../types/floorLayout';
import { ParkingType, ParkingAddon } from '../types/parking';
import { uploadFloorLayout } from '../services/uploadService';
import { parkingFloorService } from '../services/parkingService';
import { useHistory } from '../hooks/useHistory';
import { getParkingIconByColor } from '../utils/iconMapping';
import '../styles/FloorLayoutEditor.css';

interface FabricFloorLayoutEditorProps {
  workspaceId: string;
  floorId: string;
  projectId: string;
  entityType: EntityType;
  floorName: string;
  projectName: string;
  layoutImage?: FloorLayoutImage;
  spaceDefaults?: { width: number; height: number };
  parkingTypes?: ParkingType[];
  parkingAddons?: ParkingAddon[];
  spaces: FloorSpace[];
  onClose: () => void;
  onSave: (updates: SpaceUpdateData[]) => Promise<void>;
  onSpaceClick?: (spaceId: string) => void;
}

// Car SVG path (from svgrepo.com)
const CAR_SVG_PATH = `M 29.395,0 H 17.636 c -3.117,0 -5.643,3.467 -5.643,6.584 v 34.804 c 0,3.116 2.526,5.644 5.643,5.644 h 11.759 c 3.116,0 5.644,-2.527 5.644,-5.644 V 6.584 C 35.037,3.467 32.511,0 29.395,0 Z M 34.05,14.188 v 11.665 l -2.729,0.351 v -4.806 z M 32.618,10.773 c -1.016,3.9 -2.219,8.51 -2.219,8.51 H 16.631 l -2.222,-8.51 c 0,0 8.883,-3.018 18.209,0 z M 15.741,21.713 v 4.492 l -2.73,-0.349 V 14.502 Z M 13.011,37.938 V 27.579 l 2.73,0.343 v 8.196 z M 14.568,40.882 l 2.218,-3.336 h 13.771 l 2.219,3.336 z M 31.321,35.805 v -7.872 l 2.729,-0.355 v 10.048 z`;

const FabricFloorLayoutEditor: React.FC<FabricFloorLayoutEditorProps> = ({
  workspaceId,
  floorId,
  projectId,
  entityType,
  floorName,
  projectName,
  layoutImage,
  spaceDefaults = { width: 150, height: 75 },
  parkingTypes = [],
  parkingAddons = [],
  spaces,
  onClose,
  onSave,
  onSpaceClick
}) => {
  const [isEditMode, setIsEditMode] = useState(true);
  const [selectedSpaceIds, setSelectedSpaceIds] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [localSpaces, setLocalSpaces] = useState<FloorSpace[]>(spaces);
  const [showAddSpaceModal, setShowAddSpaceModal] = useState(false);
  const [currentSpaceDefaults, setCurrentSpaceDefaults] = useState(spaceDefaults);
  const [showSpaceDefaultsModal, setShowSpaceDefaultsModal] = useState(false);
  const [iconScale, setIconScale] = useState(1);
  const [modifiedSpaceIds, setModifiedSpaceIds] = useState<Set<string>>(new Set());

  const {
    state: spacePositions,
    set: setSpacePositions,
    undo,
    redo,
    canUndo,
    canRedo,
    reset: resetHistory
  } = useHistory<Map<string, SpaceObjectData>>(new Map());

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Track if initial positions have been loaded
  const initialPositionsLoaded = useRef(false);

  // Sync localSpaces with spaces prop only on initial load
  useEffect(() => {
    if (!initialPositionsLoaded.current) {
      setLocalSpaces(spaces);
    }
  }, [spaces]);

  // Sync spaceDefaults with props
  useEffect(() => {
    setCurrentSpaceDefaults(spaceDefaults);
  }, [spaceDefaults]);

  // Initialize space positions only once on first load
  useEffect(() => {
    if (initialPositionsLoaded.current) return;
    if (spaces.length === 0) return;

    const initialPositions = new Map<string, SpaceObjectData>();
    spaces.forEach((space) => {
      if (space.spaceObject) {
        initialPositions.set(space._id, { ...space.spaceObject });
      }
    });
    resetHistory(initialPositions);
    initialPositionsLoaded.current = true;
  }, [spaces, resetHistory]);

  // Initialize Fabric canvas (only once)
  useEffect(() => {
    if (!canvasRef.current || fabricCanvasRef.current) return;

    const container = containerRef.current;
    if (!container) return;

    // Use fixed dimensions to ensure consistency between editor and viewer
    const canvasWidth = 1200;
    const canvasHeight = 800;

    const canvas = new fabric.Canvas(canvasRef.current, {
      width: canvasWidth,
      height: canvasHeight,
      backgroundColor: '#f5f5f5',
      selection: true, // Always enable selection
    });

    fabricCanvasRef.current = canvas;

    // Cleanup
    return () => {
      canvas.dispose();
      fabricCanvasRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only initialize once, never dispose unless component unmounts

  // Load/update layout image when it changes
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    if (layoutImage?.url) {
      fabric.Image.fromURL(layoutImage.url, (img: any) => {
        if (!img || !canvas.getContext()) return;

        const scale = Math.min(
          (canvas.width || 1) / (img.width || 1),
          (canvas.height || 1) / (img.height || 1)
        ) * 0.9;

        img.scale(scale);
        img.set({
          left: ((canvas.width || 0) - (img.width || 0) * scale) / 2,
          top: ((canvas.height || 0) - (img.height || 0) * scale) / 2,
          selectable: false,
          evented: false,
        });

        canvas.setBackgroundImage(img, () => {
          canvas.renderAll();
        });
      });
    }
  }, [layoutImage]);

  // Update canvas selection mode when edit mode changes
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    canvas.selection = isEditMode;

    // Update all objects' selectability
    canvas.getObjects().forEach((obj: any) => {
      if (obj.spaceId) {
        obj.set({
          selectable: isEditMode,
          hasControls: isEditMode,
          hasBorders: isEditMode,
          evented: isEditMode,
        });
        obj.setCoords(); // Update object coordinates for proper hit detection
      }
    });

    canvas.renderAll();
  }, [isEditMode]);

  // Helper function for type-based colors
  const getTypeColor = useCallback((typeName: string, isPremium: boolean, status: string) => {
    // Find the parking type by name
    const parkingType = parkingTypes.find(pt => pt.name === typeName);

    if (parkingType) {
      // Use selectedColor for premium spaces, standardColor for standard spaces
      return isPremium ? (parkingType.selectedColor || '#2196F3') : (parkingType.standardColor || '#4CAF50');
    }

    // Fallback to status-based colors if type not found
    switch (status) {
      case 'AVAILABLE': return '#4CAF50';
      case 'RESERVED': return '#FFC107';
      case 'SOLD': return '#F44336';
      default: return '#9E9E9E';
    }
  }, [parkingTypes]);

  // Helper function to get icon path based on type
  const getTypeIconPath = useCallback((typeName: string, isPremium: boolean, status: string): string | null => {
    // Find the parking type by name
    const parkingType = parkingTypes.find(pt => pt.name === typeName);

    if (parkingType) {
      // Use selectedImage for premium spaces, standardImage for standard spaces
      const imagePath = isPremium ? (parkingType as any).selectedImage : (parkingType as any).standardImage;

      // If image path exists, return it
      if (imagePath) {
        return imagePath;
      }

      // Fallback: try to get image from color mapping (for backward compatibility)
      const color = isPremium ? (parkingType.selectedColor || '#2196F3') : (parkingType.standardColor || '#4CAF50');
      return getParkingIconByColor(color);
    }

    return null;
  }, [parkingTypes]);

  // Create car shape
  const createCarShape = useCallback((space: FloorSpace, position: SpaceObjectData, callback: (group: fabric.Group) => void) => {
    const width = (position.width || currentSpaceDefaults.width) * iconScale;
    const height = (position.height || currentSpaceDefaults.height) * iconScale;

    // Get icon path for this space
    const iconPath = getTypeIconPath(space.type, space.isPremium || false, space.status);

    const createGroupWithShape = (shapeElement: fabric.Object) => {
      // Add custom data
      (shapeElement as any).spaceId = space._id;
      (shapeElement as any).spaceNumber = space.number;

      // Position the shape at center of group
      shapeElement.set({
        left: 0,
        top: 0,
        originX: 'center',
        originY: 'center',
      });

      // Add number label at center of shape - use numberInt if available, otherwise parse from number
      const displayNumber = space.numberInt !== undefined ? String(space.numberInt) : space.number.replace(/\D/g, '') || space.number;
      const text = new fabric.Text(displayNumber, {
        fontSize: 14,
        fill: '#000000',
        fontWeight: 'bold',
        left: 0,
        top: 0,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
      });

      // Create addon color dots if addons exist - position them relative to shape height
      const addonDots: fabric.Circle[] = [];
      if (space.addons && space.addons.length > 0) {
        const dotRadius = 4;
        const dotSpacing = 10;
        const totalAddons = space.addons.length;
        const shapeHeight = (shapeElement.getScaledHeight ? shapeElement.getScaledHeight() : height);

        space.addons.forEach((addon, index) => {
          if (addon.color) {
            const dot = new fabric.Circle({
              radius: dotRadius,
              fill: addon.color,
              stroke: '#ffffff',
              strokeWidth: 1.5,
              // Position dots at top-middle of shape, stacked horizontally
              left: -((totalAddons - 1) * dotSpacing / 2) + (index * dotSpacing),
              top: -(shapeHeight / 2) - dotRadius - 2,
              originX: 'center',
              originY: 'center',
              selectable: false,
              evented: false,
            });
            addonDots.push(dot);
          }
        });
      }

      // Group shape, text, and addon dots, then position the group
      const groupItems: any[] = [shapeElement, text, ...addonDots];
      const group = new fabric.Group(groupItems, {
        left: position.left || 0,
        top: position.top || 0,
        angle: position.angle || 0,
        originX: 'center',
        originY: 'center',
        selectable: true,
        hasControls: true,
        hasBorders: true,
        evented: true,
        lockScalingFlip: true,
      });

      (group as any).spaceId = space._id;
      (group as any).spaceNumber = space.number;

      callback(group);
    };

    // If we have an icon path, load the image
    if (iconPath) {
      fabric.Image.fromURL(iconPath, (img: fabric.Image) => {
        if (img && img.width && img.height) {
          const scale = Math.min(width / img.width, height / img.height);
          img.set({
            scaleX: scale,
            scaleY: scale,
            left: 0,
            top: 0,
            originX: 'center',
            originY: 'center',
            selectable: false,
            evented: false,
            lockScalingFlip: true,
          });
          createGroupWithShape(img);
        } else {
          // Fallback to SVG path if image fails to load
          const path = new fabric.Path(CAR_SVG_PATH, {
            fill: getTypeColor(space.type, space.isPremium || false, space.status),
            stroke: '#000000',
            strokeWidth: 1,
            scaleX: width / 47.032,
            scaleY: height / 47.032,
            left: 0,
            top: 0,
            originX: 'center',
            originY: 'center',
            selectable: false,
            evented: false,
            lockScalingFlip: true,
          });
          createGroupWithShape(path);
        }
      }, { crossOrigin: 'anonymous' });
    } else {
      // Fallback to SVG path if no icon path found
      const path = new fabric.Path(CAR_SVG_PATH, {
        fill: getTypeColor(space.type, space.isPremium || false, space.status),
        stroke: '#000000',
        strokeWidth: 1,
        scaleX: width / 47.032,
        scaleY: height / 47.032,
        left: 0,
        top: 0,
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
        lockScalingFlip: true,
      });
      createGroupWithShape(path);
    }
  }, [currentSpaceDefaults, iconScale, getTypeColor, getTypeIconPath]);

  // Render spaces on canvas
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Clear existing space objects
    const objectsToRemove = canvas.getObjects().filter((obj: any) => obj.spaceId);
    objectsToRemove.forEach((obj: any) => canvas.remove(obj));

    // Add space objects
    localSpaces.forEach(space => {
      const position = spacePositions.get(space._id);
      if (position) {
        createCarShape(space, position, (shape) => {
          // Apply current edit mode state
          shape.set({
            selectable: isEditMode,
            hasControls: isEditMode,
            hasBorders: isEditMode,
            evented: isEditMode,
          });
          shape.setCoords();
          canvas.add(shape);
          canvas.renderAll();
        });
      }
    });
  }, [localSpaces, spacePositions, createCarShape, iconScale, isEditMode, parkingTypes]);

  const handleSelection = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const activeObjects = canvas.getActiveObjects();
    const ids = new Set<string>();
    activeObjects.forEach((obj: any) => {
      if (obj.spaceId) {
        ids.add(obj.spaceId);
      }
    });
    setSelectedSpaceIds(ids);
  }, []);

  const handleObjectModified = useCallback((e: fabric.IEvent) => {
    const obj = e.target;
    if (!obj) return;

    // Handle multi-selection (ActiveSelection)
    if (obj.type === 'activeSelection' || obj.type === 'activeselection') {
      const activeSelection = obj as fabric.ActiveSelection;
      const objects = activeSelection.getObjects();
      const groupScaleX = activeSelection.scaleX || 1;
      const groupScaleY = activeSelection.scaleY || 1;
      const groupAngle = activeSelection.angle || 0;

      // Check if there's any meaningful change
      const groupScaleChanged = Math.abs(groupScaleX - 1) > 0.001 || Math.abs(groupScaleY - 1) > 0.001;
      const groupAngleChanged = Math.abs(groupAngle) > 0.1;

      if (!groupScaleChanged && !groupAngleChanged) {
        // Only position changed - update each object's position
        const newPositions = new Map(spacePositions);
        let anyChanged = false;

        objects.forEach((child: any) => {
          if (!child.spaceId) return;
          const spaceId = child.spaceId;
          const currentPosition = spacePositions.get(spaceId);
          if (!currentPosition) return;

          // Get the object's absolute position (relative to canvas, not group)
          const absPos = fabric.util.transformPoint(
            new fabric.Point(child.left || 0, child.top || 0),
            activeSelection.calcTransformMatrix()
          );

          const positionChanged =
            Math.abs(absPos.x - currentPosition.left) > 0.1 ||
            Math.abs(absPos.y - currentPosition.top) > 0.1;

          if (positionChanged) {
            newPositions.set(spaceId, {
              ...currentPosition,
              left: absPos.x,
              top: absPos.y,
            });
            setModifiedSpaceIds(prev => new Set(prev).add(spaceId));
            anyChanged = true;
          }
        });

        if (anyChanged) {
          setSpacePositions(newPositions);
          setHasChanges(true);
          console.log('Multi-selection moved');
        }
        return;
      }

      // Scale or rotation changed - update each object
      const newPositions = new Map(spacePositions);

      objects.forEach((child: any) => {
        if (!child.spaceId) return;
        const spaceId = child.spaceId;
        const currentPosition = spacePositions.get(spaceId);
        if (!currentPosition) return;

        // Get the object's absolute position
        const absPos = fabric.util.transformPoint(
          new fabric.Point(child.left || 0, child.top || 0),
          activeSelection.calcTransformMatrix()
        );

        // Calculate new dimensions
        const currentWidth = currentPosition.width || currentSpaceDefaults.width;
        const currentHeight = currentPosition.height || currentSpaceDefaults.height;
        const newWidth = currentWidth * groupScaleX;
        const newHeight = currentHeight * groupScaleY;
        const newAngle = (currentPosition.angle || 0) + groupAngle;

        newPositions.set(spaceId, {
          ...currentPosition,
          left: absPos.x,
          top: absPos.y,
          width: newWidth,
          height: newHeight,
          angle: newAngle,
        });
        setModifiedSpaceIds(prev => new Set(prev).add(spaceId));
      });

      // Reset group scale
      activeSelection.set({ scaleX: 1, scaleY: 1, angle: 0 });

      setSpacePositions(newPositions);
      setHasChanges(true);
      console.log('Multi-selection scaled/rotated', { groupScaleX, groupScaleY, groupAngle });
      return;
    }

    // Handle single object modification
    if (!(obj as any).spaceId) return;

    const spaceId = (obj as any).spaceId;
    const currentPosition = spacePositions.get(spaceId);
    if (!currentPosition) return;

    // Get current values
    const scaleX = obj.scaleX || 1;
    const scaleY = obj.scaleY || 1;
    const newLeft = obj.left || 0;
    const newTop = obj.top || 0;
    const newAngle = obj.angle || 0;

    // Check if this is a meaningful change (not just a re-render)
    // Skip if scale is 1 and position/angle haven't changed
    const positionChanged =
      Math.abs(newLeft - currentPosition.left) > 0.1 ||
      Math.abs(newTop - currentPosition.top) > 0.1 ||
      Math.abs(newAngle - (currentPosition.angle || 0)) > 0.1;
    const scaleChanged = Math.abs(scaleX - 1) > 0.001 || Math.abs(scaleY - 1) > 0.001;

    if (!positionChanged && !scaleChanged) {
      return; // No meaningful change, skip
    }

    // Calculate new dimensions from scale
    // position.width stores BASE width (without iconScale)
    // Visual size = BASE width * iconScale
    // When user scales, obj.scaleX is applied to visual size
    // New BASE width = currentPosition.width * obj.scaleX
    const currentWidth = currentPosition.width || currentSpaceDefaults.width;
    const currentHeight = currentPosition.height || currentSpaceDefaults.height;
    const newWidth = currentWidth * scaleX;
    const newHeight = currentHeight * scaleY;

    const updatedPosition: SpaceObjectData = {
      ...currentPosition,
      left: newLeft,
      top: newTop,
      width: newWidth,
      height: newHeight,
      angle: newAngle,
    };

    // Reset scale to 1 after applying dimensions (only if scale changed)
    if (scaleChanged) {
      obj.set({ scaleX: 1, scaleY: 1 });
    }

    const newPositions = new Map(spacePositions);
    newPositions.set(spaceId, updatedPosition);
    setSpacePositions(newPositions);

    // Track this space as modified
    setModifiedSpaceIds(prev => new Set(prev).add(spaceId));
    setHasChanges(true);
    console.log('Space modified:', spaceId, { scaleChanged, positionChanged, newWidth, newHeight });
  }, [spacePositions, currentSpaceDefaults, setSpacePositions]);

  // Attach/re-attach event handlers whenever they change
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Remove old handlers
    canvas.off('selection:created');
    canvas.off('selection:updated');
    canvas.off('selection:cleared');
    canvas.off('object:modified');

    // Attach new handlers
    canvas.on('selection:created', handleSelection);
    canvas.on('selection:updated', handleSelection);
    canvas.on('selection:cleared', () => setSelectedSpaceIds(new Set()));
    canvas.on('object:modified', handleObjectModified);

    console.log('Event handlers attached/refreshed');
  }, [handleSelection, handleObjectModified]);

  const handleSave = async () => {
    if (!hasChanges || modifiedSpaceIds.size === 0) return;

    setIsSaving(true);
    try {
      const updates: SpaceUpdateData[] = [];
      // Only save spaces that were actually modified
      modifiedSpaceIds.forEach((spaceId) => {
        const position = spacePositions.get(spaceId);
        if (position) {
          console.log('[EDITOR] Saving position for space', spaceId, ':', position);
          updates.push({
            id: spaceId,
            spaceObject: position
          });
        }
      });

      await onSave(updates);
      setHasChanges(false);
      setModifiedSpaceIds(new Set()); // Clear modified tracking after successful save
    } catch (error) {
      console.error('Error saving floor layout:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddSpace = async (newSpace: FloorSpace) => {
    setLocalSpaces([...localSpaces, newSpace]);
    const initialPosition: SpaceObjectData = {
      left: 400,
      top: 300,
      width: currentSpaceDefaults.width,
      height: currentSpaceDefaults.height,
      angle: 0
    };
    const newPositions = new Map(spacePositions);
    newPositions.set(newSpace._id, initialPosition);
    setSpacePositions(newPositions);
    // Track newly added space as modified
    setModifiedSpaceIds(prev => new Set(prev).add(newSpace._id));
    setHasChanges(true);
    setShowAddSpaceModal(false);
  };

  const handleDeleteSpace = async () => {
    if (selectedSpaceIds.size === 0) return;

    const confirmMessage = selectedSpaceIds.size === 1
      ? 'Are you sure you want to delete this space?'
      : `Are you sure you want to delete ${selectedSpaceIds.size} spaces?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      // Delete from canvas
      const canvas = fabricCanvasRef.current;
      if (canvas) {
        const objectsToRemove = canvas.getObjects().filter((obj: any) =>
          selectedSpaceIds.has(obj.spaceId)
        );
        objectsToRemove.forEach(obj => canvas.remove(obj));
        canvas.discardActiveObject();
        canvas.renderAll();
      }

      // Delete from backend
      if (entityType === 'parking') {
        await Promise.all(
          Array.from(selectedSpaceIds).map(id =>
            parkingFloorService.deleteSpace(workspaceId, id)
          )
        );
      } else if (entityType === 'locker') {
        const { lockerSpaceService } = await import('../services/lockerService');
        await Promise.all(
          Array.from(selectedSpaceIds).map(id =>
            lockerSpaceService.delete(workspaceId, id)
          )
        );
      } else if (entityType === 'bicycle') {
        const { bicycleSpaceService } = await import('../services/bicycleService');
        await Promise.all(
          Array.from(selectedSpaceIds).map(id =>
            bicycleSpaceService.delete(workspaceId, id)
          )
        );
      }

      // Update local state
      setLocalSpaces(localSpaces.filter(space => !selectedSpaceIds.has(space._id)));
      const newPositions = new Map(spacePositions);
      selectedSpaceIds.forEach(id => newPositions.delete(id));
      setSpacePositions(newPositions);
      setSelectedSpaceIds(new Set());
      setHasChanges(true);
    } catch (error) {
      console.error('Error deleting spaces:', error);
      toast.error('Failed to delete spaces. Please try again.');
    }
  };

  // Alignment functions for multiple selected spaces
  const handleAlign = useCallback((alignment: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => {
    if (selectedSpaceIds.size < 2) return;

    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Get positions of selected spaces
    const selectedPositions: { id: string; position: SpaceObjectData }[] = [];
    selectedSpaceIds.forEach(id => {
      const position = spacePositions.get(id);
      if (position) {
        selectedPositions.push({ id, position });
      }
    });

    if (selectedPositions.length < 2) return;

    // Calculate alignment reference point
    let referenceValue: number;
    const newPositions = new Map(spacePositions);

    switch (alignment) {
      case 'left':
        referenceValue = Math.min(...selectedPositions.map(p => p.position.left));
        selectedPositions.forEach(({ id, position }) => {
          newPositions.set(id, { ...position, left: referenceValue });
          setModifiedSpaceIds(prev => new Set(prev).add(id));
        });
        break;
      case 'center':
        const minLeft = Math.min(...selectedPositions.map(p => p.position.left));
        const maxLeft = Math.max(...selectedPositions.map(p => p.position.left));
        referenceValue = (minLeft + maxLeft) / 2;
        selectedPositions.forEach(({ id, position }) => {
          newPositions.set(id, { ...position, left: referenceValue });
          setModifiedSpaceIds(prev => new Set(prev).add(id));
        });
        break;
      case 'right':
        referenceValue = Math.max(...selectedPositions.map(p => p.position.left));
        selectedPositions.forEach(({ id, position }) => {
          newPositions.set(id, { ...position, left: referenceValue });
          setModifiedSpaceIds(prev => new Set(prev).add(id));
        });
        break;
      case 'top':
        referenceValue = Math.min(...selectedPositions.map(p => p.position.top));
        selectedPositions.forEach(({ id, position }) => {
          newPositions.set(id, { ...position, top: referenceValue });
          setModifiedSpaceIds(prev => new Set(prev).add(id));
        });
        break;
      case 'middle':
        const minTop = Math.min(...selectedPositions.map(p => p.position.top));
        const maxTop = Math.max(...selectedPositions.map(p => p.position.top));
        referenceValue = (minTop + maxTop) / 2;
        selectedPositions.forEach(({ id, position }) => {
          newPositions.set(id, { ...position, top: referenceValue });
          setModifiedSpaceIds(prev => new Set(prev).add(id));
        });
        break;
      case 'bottom':
        referenceValue = Math.max(...selectedPositions.map(p => p.position.top));
        selectedPositions.forEach(({ id, position }) => {
          newPositions.set(id, { ...position, top: referenceValue });
          setModifiedSpaceIds(prev => new Set(prev).add(id));
        });
        break;
    }

    setSpacePositions(newPositions);
    setHasChanges(true);
  }, [selectedSpaceIds, spacePositions, setSpacePositions]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      // Upload image to S3
      const result = await uploadFloorLayout(workspaceId, file, entityType, floorId);

      // Get image dimensions
      const img = new Image();
      img.src = result.url;
      await new Promise((resolve) => {
        img.onload = resolve;
      });

      // Save layout information to floor document
      if (entityType === 'parking') {
        await parkingFloorService.uploadLayout(workspaceId, floorId, {
          url: result.url,
          key: result.key,
          width: img.width,
          height: img.height,
          size: result.size,
          mimeType: result.mimeType
        });
      } else if (entityType === 'locker') {
        const { lockerFloorService } = await import('../services/lockerService');
        await lockerFloorService.uploadLayout(workspaceId, floorId, {
          url: result.url,
          key: result.key,
          width: img.width,
          height: img.height,
          size: result.size,
          mimeType: result.mimeType
        });
      } else if (entityType === 'bicycle') {
        const { bicycleFloorService } = await import('../services/bicycleService');
        await bicycleFloorService.uploadLayout(workspaceId, floorId, {
          url: result.url,
          key: result.key,
          width: img.width,
          height: img.height,
          size: result.size,
          mimeType: result.mimeType
        });
      }

      // Update background image on canvas
      const canvas = fabricCanvasRef.current;
      if (canvas && result.url) {
        fabric.Image.fromURL(result.url, (fabricImg: any) => {
          const currentCanvas = fabricCanvasRef.current;
          if (!fabricImg || !currentCanvas || !currentCanvas.getContext()) return;

          const scale = Math.min(
            (currentCanvas.width || 1) / (fabricImg.width || 1),
            (currentCanvas.height || 1) / (fabricImg.height || 1)
          ) * 0.9;

          fabricImg.scale(scale);
          fabricImg.set({
            left: ((currentCanvas.width || 0) - (fabricImg.width || 0) * scale) / 2,
            top: ((currentCanvas.height || 0) - (fabricImg.height || 0) * scale) / 2,
            selectable: false,
            evented: false,
          });

          currentCanvas.setBackgroundImage(fabricImg, () => {
            if (currentCanvas.getContext()) {
              currentCanvas.renderAll();
            }
          });
        });
      }

      toast.success('Layout image uploaded successfully!');
      setHasChanges(false); // Layout save is independent of space changes
    } catch (error) {
      console.error('Error uploading floor layout:', error);
      toast.error('Failed to upload floor layout image. Please try again.');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const selectedSpace = selectedSpaceIds.size === 1
    ? localSpaces.find(s => selectedSpaceIds.has(s._id))
    : null;

  const selectedSpaces = localSpaces.filter(s => selectedSpaceIds.has(s._id));

  return (
    <div className="floor-layout-editor">
      {/* Toolbar */}
      <div className="floor-layout-toolbar">
        <div className="toolbar-left">
          <button onClick={onClose} className="btn btn-secondary btn-sm">
            ← Back
          </button>
          <h2>{floorName} - {projectName}</h2>
        </div>

        <div className="toolbar-center">
          <button
            onClick={() => setIsEditMode(!isEditMode)}
            className={isEditMode ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
          >
            {isEditMode ? '✓ Edit Mode' : 'View Mode'}
          </button>

          {isEditMode && (
            <>
              <div className="icon-scale-controls">
                <button
                  onClick={() => setIconScale(prev => Math.max(0.5, prev - 0.25))}
                  className="btn btn-secondary btn-sm"
                  disabled={iconScale <= 0.5}
                >
                  -
                </button>
                <span>Icons</span>
                <button
                  onClick={() => setIconScale(prev => Math.min(3, prev + 0.25))}
                  className="btn btn-secondary btn-sm"
                  disabled={iconScale >= 3}
                >
                  +
                </button>
              </div>

              <button
                onClick={undo}
                disabled={!canUndo}
                className="btn btn-secondary btn-sm"
              >
                ↶ Undo
              </button>
              <button
                onClick={redo}
                disabled={!canRedo}
                className="btn btn-secondary btn-sm"
              >
                ↷ Redo
              </button>

              {/* Alignment buttons - show when 2+ spaces selected */}
              {selectedSpaceIds.size >= 2 && (
                <div className="alignment-controls" style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px', borderLeft: '1px solid #E5E7EB', paddingLeft: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#6B7280', marginRight: '4px' }}>Align:</span>
                  <button
                    onClick={() => handleAlign('left')}
                    className="btn btn-secondary btn-sm"
                    title="Align Left"
                    style={{ padding: '4px 6px' }}
                  >
                    <AlignLeft size={14} />
                  </button>
                  <button
                    onClick={() => handleAlign('center')}
                    className="btn btn-secondary btn-sm"
                    title="Align Center (Horizontal)"
                    style={{ padding: '4px 6px' }}
                  >
                    <AlignCenter size={14} />
                  </button>
                  <button
                    onClick={() => handleAlign('right')}
                    className="btn btn-secondary btn-sm"
                    title="Align Right"
                    style={{ padding: '4px 6px' }}
                  >
                    <AlignRight size={14} />
                  </button>
                  <div style={{ width: '1px', height: '20px', backgroundColor: '#E5E7EB', margin: '0 4px' }} />
                  <button
                    onClick={() => handleAlign('top')}
                    className="btn btn-secondary btn-sm"
                    title="Align Top"
                    style={{ padding: '4px 6px' }}
                  >
                    <AlignStartVertical size={14} />
                  </button>
                  <button
                    onClick={() => handleAlign('middle')}
                    className="btn btn-secondary btn-sm"
                    title="Align Middle (Vertical)"
                    style={{ padding: '4px 6px' }}
                  >
                    <AlignCenterVertical size={14} />
                  </button>
                  <button
                    onClick={() => handleAlign('bottom')}
                    className="btn btn-secondary btn-sm"
                    title="Align Bottom"
                    style={{ padding: '4px 6px' }}
                  >
                    <AlignEndVertical size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="toolbar-right">
          {isEditMode && hasChanges && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn btn-primary btn-md"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {isSaving ? 'Saving...' : <><Save size={16} /> Save Changes</>}
            </button>
          )}
        </div>
      </div>

      {/* Main content area with canvas and side panel */}
      <div className="floor-layout-main">
        <div ref={containerRef} className="canvas-container">
          <canvas ref={canvasRef} />
        </div>

        {/* Side panel */}
        {isEditMode && (
          <div className="side-panel">
          <div className="panel-section">
            <h3>Floor Layout</h3>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className="btn btn-secondary btn-md full-width"
              style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
            >
              {uploadingImage ? 'Uploading...' : layoutImage ? <><RefreshCw size={16} /> Replace Image</> : <><Upload size={16} /> Upload Image</>}
            </button>
          </div>

          <div className="panel-section">
            <h3>Space Management</h3>
            <button onClick={() => setShowAddSpaceModal(true)} className="btn btn-primary btn-md full-width" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
              <Plus size={16} /> Add Space
            </button>
            <button onClick={() => setShowSpaceDefaultsModal(true)} className="btn btn-secondary btn-md full-width" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
              <Settings size={16} /> Configure Size
            </button>
            {selectedSpaceIds.size > 0 && (
              <button onClick={handleDeleteSpace} className="btn btn-danger btn-md full-width" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                <Trash2 size={16} /> Delete Space{selectedSpaceIds.size > 1 ? 's' : ''}
              </button>
            )}
          </div>

          {selectedSpaceIds.size > 0 && (
            <div className="panel-section">
              <h3>{selectedSpaceIds.size > 1 ? `${selectedSpaceIds.size} Spaces Selected` : 'Selected Space'}</h3>
              {selectedSpace ? (
                <div className="space-details">
                  <div className="detail-row">
                    <span className="detail-label">Number</span>
                    <span className="detail-value">{selectedSpace.number}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status</span>
                    <span className="detail-value">{selectedSpace.status}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Type</span>
                    <span className="detail-value">Single Car Parking</span>
                  </div>
                </div>
              ) : (
                <div className="space-details">
                  {selectedSpaces.map(space => (
                    <div key={space._id} className="detail-row">
                      <span className="detail-value">{space.number}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="panel-section">
            <h3>Summary</h3>
            <div className="summary-stats">
              <div className="stat-row">
                <span className="stat-label">Total Spaces</span>
                <span className="stat-value">{localSpaces.length}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">AVAILABLE</span>
                <span className="stat-value">
                  {localSpaces.filter(s => s.status === 'AVAILABLE').length}
                </span>
              </div>
            </div>
          </div>

          <h4 style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginTop: '24px',
            marginBottom: '16px'
          }}>
            Type Legend
          </h4>
          <TypeLegend
            parkingTypes={parkingTypes}
          />

          {parkingAddons.length > 0 && (
            <>
              <h4 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginTop: '24px',
                marginBottom: '16px'
              }}>
                Addon Legend
              </h4>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {parkingAddons.map((addon) => (
                  <div key={addon._id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: addon.color || '#FFA500',
                      border: '1.5px solid #ffffff',
                      boxShadow: '0 0 0 1px #D1D5DB',
                      flexShrink: 0
                    }} />
                    <span style={{ fontSize: '13px', color: '#374151' }}>
                      {addon.name}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {/* Modals */}
      <AddSpaceModal
        isOpen={showAddSpaceModal}
        workspaceId={workspaceId}
        floorId={floorId}
        floorName={floorName}
        projectId={projectId}
        entityType={entityType}
        onClose={() => setShowAddSpaceModal(false)}
        onSuccess={handleAddSpace}
      />

      <SpaceDefaultsModal
        isOpen={showSpaceDefaultsModal}
        entityType={entityType}
        currentDefaults={currentSpaceDefaults}
        onClose={() => setShowSpaceDefaultsModal(false)}
        onSave={(newDefaults) => {
          setCurrentSpaceDefaults(newDefaults);
          setShowSpaceDefaultsModal(false);
          setHasChanges(true);
        }}
      />
    </div>
  );
};

export default FabricFloorLayoutEditor;
